import React, { useState } from "react";
import { withPreviewRoot } from "../../../hoc/withPreviewRoot";
import credentialsStyle from "./Credential.module.css";
import AlertPopup from "../../AlertPopup";

const Credentials = ({ appData }) => {
  const [copiedKey, setCopiedKey] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });

  // ─────────────────────────────────────────────
  // Bot readiness check
  // An app can only go live when at least one bot is configured.
  // ─────────────────────────────────────────────
  const isAnyBotEnabled =
    appData?.enabled_bot_content === true ||
    appData?.ai_agent_bot_enabled === true ||
    appData?.live_agent_enabled === true;

  const configuredBots = [
    appData?.enabled_bot_content && "FAQ + Lead Generation",
    appData?.ai_agent_bot_enabled && "AI Chat",
    appData?.live_agent_enabled && "Live Agent",
  ].filter(Boolean);

  const copyToClipboard = (text, keyName) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.left = "-99999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    setCopiedKey(keyName);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const maskValue = (value) => {
    if (!value) return "";
    return value.slice(0, 4) + "••••••••••••••••" + value.slice(-4);
  };

  const handleGoLiveFromDraft = async () => {
    if (!appData?.draft) return;

    // Hard gate — at least one bot must be configured.
    // This prevents an app with no chat behind it from being published.
    // The server also enforces this via a DynamoDB ConditionExpression on
    // /publish_app; this client-side check is a UX hint, not the source of truth.
    if (!isAnyBotEnabled) {
      setAlert({
        show: true,
        type: "error",
        message:
          "You need at least one bot configured before publishing this app. Open the app settings to configure one.",
      });
      return;
    }

    setUpdating(true);
    setAlert({ show: false, type: "", message: "" });

    try {
      const token = localStorage.getItem("token");
      const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;
      const region_prefix = appData.region.toLowerCase();

      const response = await fetch(
        `https://${region_prefix}.apps2.${backend_api_base_url}/publish_app?act_type=tenant&app_name=${appData.app_name}`,
        {
          method: "PUT",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ draft: false }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to publish app");
      }

      setAlert({
        show: true,
        type: "success",
        message: "App is now live! Draft status removed.",
      });

      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error("Error making app live:", error);
      setAlert({
        show: true,
        type: "error",
        message: error.message || "Failed to make app live. Please try again.",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, show: false });
  };

  return (
    <div className={credentialsStyle.container}>
      <h2 className={credentialsStyle.title}>Credentials</h2>

      {/* Draft / Live badge */}
      {appData?.draft ? (
        <div className={credentialsStyle.draftSection}>
          <div className={credentialsStyle.draftBadge}>DRAFT</div>

          <button
            onClick={handleGoLiveFromDraft}
            disabled={updating || !isAnyBotEnabled}
            className={`${credentialsStyle.goLiveBtn} ${
              !isAnyBotEnabled ? credentialsStyle.goLiveBtnDisabled : ""
            }`}
            title={
              !isAnyBotEnabled
                ? "Configure at least one bot before going live"
                : "Publish this app"
            }
          >
            {updating ? "Processing..." : "Go Live..."}
          </button>

          {isAnyBotEnabled ? (
            <p className={credentialsStyle.draftNote}>
              This app is in draft mode. Click "Go Live" to publish it.
              <br />
              <span className={credentialsStyle.draftNoteSuccess}>
                ✅ Configured: {configuredBots.join(", ")}
              </span>
            </p>
          ) : (
            <div className={credentialsStyle.draftBlockedNote}>
              <p className={credentialsStyle.draftBlockedTitle}>
                ⚠️ Configure at least one bot before going live
              </p>
              <p className={credentialsStyle.draftBlockedBody}>
                Your app needs at least one of the following before it can
                serve visitors:
              </p>
              <ul className={credentialsStyle.draftBlockedList}>
                <li>
                  <strong>FAQ + Lead Generation</strong> — set up an FAQ tree
                  or lead capture form
                </li>
                <li>
                  <strong>AI Chat</strong> — enable the AI bot and add at
                  least one ingestion source
                </li>
                <li>
                  <strong>Live Agent</strong> — enable live agent and set your
                  welcome message
                </li>
              </ul>
              <p className={credentialsStyle.draftBlockedFooter}>
                Once any of these is configured, the "Go Live" button will
                become available.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className={credentialsStyle.draftSection}>
          <div className={credentialsStyle.liveBadge}>LIVE NOW</div>
        </div>
      )}

      <div className={credentialsStyle.credentialsBox}>
        <table className={credentialsStyle.table}>
          <tbody>
            <tr>
              <td>App ID:</td>
              <td>
                <div className={credentialsStyle.rowFlex}>
                  <span>{appData.app_id}</span>
                </div>
              </td>
            </tr>

            <tr>
              <td style={{ fontWeight: "bold" }}>App Name:</td>
              <td style={{ fontWeight: "bold" }} data-tour="copy-app-name">
                <div className={credentialsStyle.rowFlex}>
                  <span>{appData.app_name}</span>
                  <button
                    className={credentialsStyle.copyBtn}
                    onClick={() => copyToClipboard(appData.app_name, "app_id")}
                  >
                    {copiedKey === "app_id" ? "Copied!" : "Copy"}
                  </button>
                </div>
              </td>
            </tr>

            <tr>
              <td style={{ fontWeight: "bold" }}>Auth Key:</td>
              <td style={{ fontWeight: "bold" }} data-tour="copy-auth-key">
                <div className={credentialsStyle.rowFlex}>
                  <span className={credentialsStyle.masked}>
                    {maskValue(appData.auth_key)}
                  </span>
                  <button
                    className={credentialsStyle.copyBtn}
                    onClick={() => copyToClipboard(appData.auth_key, "auth_key")}
                  >
                    {copiedKey === "auth_key" ? "Copied!" : "Copy"}
                  </button>
                </div>
              </td>
            </tr>

            {appData.rest_api_key && (
              <tr>
                <td style={{ fontWeight: "bold" }}>REST API Key:</td>
                <td style={{ fontWeight: "bold" }} data-tour="copy-rest-api-key">
                  <div className={credentialsStyle.rowFlex}>
                    <span className={credentialsStyle.masked}>
                      {maskValue(appData.rest_api_key)}
                    </span>
                    <button
                      className={credentialsStyle.copyBtn}
                      onClick={() =>
                        copyToClipboard(appData.rest_api_key, "rest_api_key")
                      }
                      
                    >
                      {copiedKey === "rest_api_key" ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </td>
              </tr>
            )}

            <tr><td>Role:</td><td>{appData.role}</td></tr>
            <tr><td>Tenant:</td><td>{appData.tenant_id}</td></tr>
            <tr><td>Tenant ID:</td><td>{appData.tenant_info?.id}</td></tr>
            <tr><td>Timestamp:</td><td>{appData.timestamp}</td></tr>
            <tr><td>Version:</td><td>{appData.settings.version}</td></tr>
            <tr><td>Type:</td><td>{appData.settings.app_type}</td></tr>
            <tr><td>Region:</td><td>{appData.region}</td></tr>
            <tr>
              <td>Status:</td>
              <td>
                <span
                  className={
                    appData?.draft
                      ? credentialsStyle.draftStatus
                      : credentialsStyle.liveStatus
                  }
                >
                  {appData?.draft ? "Draft" : "Live"}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {alert.show && (
        <AlertPopup
          type={alert.type}
          message={alert.message}
          onClose={handleCloseAlert}
        />
      )}
    </div>
  );
};

export default withPreviewRoot(Credentials);